import type { PanelMention } from '@server/panel-mentions';

import type {
  ApprovalPayload,
  InteractiveAnswer,
  InteractiveBlockKind,
  PendingBlock,
  QuestionBlockPayload,
  SSEMessage,
} from '@/_shared/types';

type ReadSummary = NonNullable<Extract<SSEMessage, { type: 'tool_result' }>['readSummary']>;

/** The server's failed-records read summary, checked field by field (as `src/db/messages.ts` does). */
function isReadSummary(value: unknown): value is ReadSummary {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    (v['runId'] === null || typeof v['runId'] === 'string') &&
    (v['status'] === null || typeof v['status'] === 'string') &&
    typeof v['rowCount'] === 'number' &&
    typeof v['possiblyMore'] === 'boolean'
  );
}

export function applySSEEvent(
  prevMessages: SSEMessage[],
  eventType: string,
  data: Record<string, unknown>,
): SSEMessage[] {
  // Defense-in-depth: if the event carries a seq and we already have a message
  // with that seq, skip the append — this catches any upstream dedup failure.
  const seq = data['seq'] as number | undefined;
  if (seq != null && prevMessages.some((m) => m.seq === seq)) {
    return prevMessages;
  }

  switch (eventType) {
    case 'user_message': {
      const content = data['content'] as string;
      const referencedPanels = data['referencedPanels'] as PanelMention[] | undefined;
      // Dedup: scan backward for a seq-less (optimistic) user_message with the
      // same content. Only seq-less messages are candidates — messages with seq
      // are persisted and canonical, so matching content across turns is
      // coincidence, not duplication.
      let turnStartsSeen = 0;
      for (let i = prevMessages.length - 1; i >= 0; i--) {
        const m = prevMessages[i]!;
        // Stop when we cross into a previous turn — the first turn_start is
        // our own turn, the second means we've left the current turn scope.
        if (m.type === 'turn_start') {
          turnStartsSeen++;
          if (turnStartsSeen >= 2) break;
          continue;
        }
        if (m.type === 'user_message' && m.seq == null && m.content === content) {
          if (seq != null) {
            // Upgrade optimistic (seq-less) message with the real seq number. The server's
            // resolved references win — it dropped any id the dashboard spec does not have.
            const updated = [...prevMessages];
            updated[i] = { ...m, seq, ...(referencedPanels && { referencedPanels }) };
            return updated;
          }
          // Both seq-less with same content — exact duplicate echo, skip.
          return prevMessages;
        }
      }
      return [...prevMessages, { type: 'user_message', content, ...(referencedPanels && { referencedPanels }), seq }];
    }

    case 'assistant':
      return [...prevMessages, { type: 'assistant', content: data['content'] as string, seq }];

    case 'tool_use':
      return [
        ...prevMessages,
        {
          type: 'tool_use',
          name: data['name'] as string,
          friendlyName: data['friendlyName'] as string,
          category: data['category'] as string,
          action: data['action'] as string,
          args: data['args'],
          id: data['id'] as string,
          summary: data['summary'] as string | undefined,
          groupedTitle: data['groupedTitle'] as string | undefined,
          detail:
            (data['detail'] as { type: 'code'; content: string; language: string } | null | undefined) ?? undefined,
          seq,
        },
      ];

    case 'tool_result':
      return [
        ...prevMessages,
        {
          type: 'tool_result',
          id: data['id'] as string,
          content: data['content'] as string,
          // The activation chat's failed-records read summary (server `failed-records-read-summary.ts`).
          ...(isReadSummary(data['readSummary']) ? { readSummary: data['readSummary'] } : {}),
          seq,
        },
      ];

    case 'ui_message':
      return [
        ...prevMessages,
        {
          type: 'ui_message',
          uiType: data['uiType'] as
            | 'text'
            | 'table'
            | 'code'
            | 'alert'
            | 'chart'
            | 'recommend_actions'
            | 'change_title'
            | 'workflow_preview'
            | 'dashboard_artifact'
            | 'audience_artifact',
          content: data['content'] as string,
          metadata: data['metadata'],
          seq,
        },
      ];

    case 'result':
      return [
        ...prevMessages,
        {
          type: 'result',
          costUsd: data['costUsd'] as number,
          turns: data['turns'] as number,
          durationMs: data['durationMs'] as number,
          seq,
        },
      ];

    case 'turn_start':
      return [
        ...prevMessages,
        {
          type: 'turn_start',
          turnId: data['turnId'] as string,
          startedAt: data['startedAt'] as number,
          ...(typeof data['runId'] === 'string' || data['runId'] === null ? { runId: data['runId'] } : {}),
          seq,
        },
      ];

    case 'turn_complete':
      return [
        ...prevMessages,
        {
          type: 'turn_complete',
          turnId: data['turnId'] as string,
          ...(data['outcome'] === 'completed' || data['outcome'] === 'interrupted' ? { outcome: data['outcome'] } : {}),
          ...(typeof data['reason'] === 'string' ? { reason: data['reason'] } : {}),
          seq,
        },
      ];

    case 'skill_invoked':
      return [
        ...prevMessages,
        {
          type: 'skill_invoked',
          skillId: data['skillId'] as string,
          title: data['title'] as string,
          seq,
        },
      ];

    case 'memory_flushed':
      return [
        ...prevMessages,
        {
          type: 'memory_flushed',
          count: data['count'] as number,
          ids: data['ids'] as string[],
          seq,
        },
      ];

    case 'compaction_started':
      return [
        ...prevMessages,
        {
          type: 'compaction_started',
          trigger: data['trigger'] as 'auto' | 'manual',
          timestamp: data['timestamp'] as number,
          seq,
        },
      ];

    case 'turn_aborted': {
      const turnId = data['turnId'] as string;
      const startIdx = prevMessages.findIndex((m) => m.type === 'turn_start' && m.turnId === turnId);
      const userMessage = typeof data['userMessage'] === 'string' ? data['userMessage'] : '';
      if (startIdx === -1) {
        // A local Stop has no saved seq and may fail. Only a saved abort marker can remove the
        // optimistic prompt before turn_start arrives.
        if (seq == null) return prevMessages;
        // The local Stop already cut the turn and left an unsaved marker. The saved one replaces it,
        // bringing its seq and the panels the prompt named, which the transcript shows.
        const localIdx = prevMessages.findIndex((m) => m.type === 'turn_aborted' && m.turnId === turnId);
        if (localIdx !== -1) {
          const local = prevMessages[localIdx];
          if (local?.type === 'turn_aborted' && local.seq != null) return prevMessages;
          const referencedPanels = data['referencedPanels'] as PanelMention[] | undefined;
          return [
            ...prevMessages.slice(0, localIdx),
            {
              type: 'turn_aborted',
              turnId,
              userMessage: userMessage || (local?.type === 'turn_aborted' ? local.userMessage : ''),
              ...(referencedPanels?.length ? { referencedPanels } : {}),
              seq,
            },
            ...prevMessages.slice(localIdx + 1),
          ];
        }
        let optimisticIdx = -1;
        if (userMessage) {
          for (let i = prevMessages.length - 1; i >= 0; i--) {
            const message = prevMessages[i];
            if (message?.type === 'user_message' && message.seq == null && message.content === userMessage) {
              optimisticIdx = i;
              break;
            }
          }
        }
        if (optimisticIdx === -1) return prevMessages;
        const optimistic = prevMessages[optimisticIdx];
        const referencedPanels =
          (data['referencedPanels'] as PanelMention[] | undefined) ??
          (optimistic?.type === 'user_message' ? optimistic.referencedPanels : undefined);
        return [
          ...prevMessages.slice(0, optimisticIdx),
          ...prevMessages.slice(optimisticIdx + 1),
          { type: 'turn_aborted', turnId, userMessage, ...(referencedPanels?.length ? { referencedPanels } : {}), seq },
        ];
      }
      // If the message immediately before turn_start matches the aborted prompt,
      // it's the dashboard sidebar's optimistic (or upgraded optimistic) — drop it too.
      const prev = prevMessages[startIdx - 1];
      const promptInTurn = prevMessages.slice(startIdx).find((m) => m.type === 'user_message');
      const dropPrev =
        prev?.type === 'user_message' && (userMessage ? prev.content === userMessage : promptInTurn === undefined);
      const cutAt = dropPrev ? startIdx - 1 : startIdx + 1;
      // The marker shows the stopped prompt in the transcript. Recovery paths send it without the
      // prompt, so it then takes the prompt from the turn being cut.
      const cutPrompt = promptInTurn ?? (dropPrev ? prev : undefined);
      const fallback = !userMessage && cutPrompt?.type === 'user_message' ? cutPrompt : undefined;
      const referencedPanels = (data['referencedPanels'] as PanelMention[] | undefined) ?? fallback?.referencedPanels;
      // Keep up to cutAt; append the abort marker so sliceIntoTurns can mark
      // the slice 'aborted' and consumers can unlock the composer.
      return [
        ...prevMessages.slice(0, cutAt),
        {
          type: 'turn_aborted',
          turnId,
          userMessage: userMessage || (fallback?.content ?? ''),
          ...(referencedPanels?.length ? { referencedPanels } : {}),
          seq: data['seq'] as number | undefined,
        },
      ];
    }

    case 'error': {
      const code = data['code'];
      const turnId = data['turnId'];
      const reason = data['reason'];
      return [
        ...prevMessages,
        {
          type: 'error',
          message: data['message'] as string,
          ...(typeof code === 'string' ? { code } : {}),
          ...(typeof turnId === 'string' ? { turnId } : {}),
          ...(data['outcome'] === 'failed' ? { outcome: 'failed' as const } : {}),
          ...(typeof reason === 'string' ? { reason } : {}),
          seq,
        },
      ];
    }

    case 'awaiting_input':
      return [
        ...prevMessages,
        {
          type: 'awaiting_input',
          blockId: data['blockId'] as string,
          kind: data['kind'] as InteractiveBlockKind,
          payload: data['payload'] as QuestionBlockPayload | ApprovalPayload,
          turnId: data['turnId'] as string,
          seq,
        },
      ];

    case 'interactive_answer': {
      const blockId = data['blockId'] as string;
      const answer = data['answer'] as InteractiveAnswer;
      // Dedup an optimistic (seq-less) answer for this block — the client adds one on submit so the
      // block clears even if SSE is slow/dead; upgrade it in place when the seq'd SSE copy arrives
      // rather than rendering the summary twice.
      const optimisticIdx = prevMessages.findIndex(
        (m) => m.type === 'interactive_answer' && m.blockId === blockId && m.seq == null,
      );
      if (optimisticIdx !== -1) {
        if (seq == null) return prevMessages; // duplicate optimistic
        const updated = [...prevMessages];
        updated[optimisticIdx] = { type: 'interactive_answer', blockId, answer, seq };
        return updated;
      }
      return [...prevMessages, { type: 'interactive_answer', blockId, answer, seq }];
    }

    case 'routine_draft':
      // Inert by design (P12 §6.3): a `routine_draft` patch is an ephemeral editor-pane control
      // event, never a transcript message. The workflow chat hook (T-FE3) applies it to the draft
      // store via useSSE's `onEvent`; here we explicitly drop it instead of letting it fall through.
      return prevMessages;

    case 'dashboard_draft':
      // Inert (AI-715): routed to the dashboard draft store via useSSE `onEvent`.
      return prevMessages;

    case 'chat_title':
      // Inert: the auto-title's name for a new thread, routed to the query cache via useSSE `onEvent`.
      // It drives the header and the chat list, never a transcript bubble.
      return prevMessages;

    case 'audience_draft':
    case 'audience_draft_count':
      // Inert (ACT2-623): routed to the audience page's local draft via useSSE `onEvent`. They drive the
      // editor pane and the size bar, never a transcript bubble.
      return prevMessages;

    default:
      return prevMessages;
  }
}

/**
 * The block currently blocking the composer, or null. A block is pending when its `awaiting_input`
 * has no matching `interactive_answer` AND no turn-terminal event followed it (a terminal without an
 * answer means the turn was interrupted/errored — the composer should show that, not stay gated).
 * Walks backward so the most recent unresolved block wins (at most one active per single-threaded turn).
 */
export function derivePendingBlock(messages: SSEMessage[]): PendingBlock | null {
  const answeredBlockIds = new Set<string>();
  let terminalSeen = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.type === 'interactive_answer') {
      answeredBlockIds.add(m.blockId);
      continue;
    }
    if (m.type === 'turn_complete' || m.type === 'error' || m.type === 'turn_aborted') {
      terminalSeen = true;
      continue;
    }
    if (m.type === 'awaiting_input') {
      if (answeredBlockIds.has(m.blockId) || terminalSeen) return null;
      // kind ↔ payload correlation is guaranteed server-side; the SSE union types them loosely.
      return { blockId: m.blockId, kind: m.kind, payload: m.payload, turnId: m.turnId } as PendingBlock;
    }
  }
  return null;
}
